import { ParseResult, Schema } from "effect";
import * as z from "zod";

/**
 * Converts a Zod schema to an Effect Schema.
 * This allows using Zod schemas in Effect Schema validation pipelines.
 */
export function zodToEffectSchema<A>(
  zodSchema: z.ZodType<A>,
): Schema.Schema<A, unknown, never> {
  return Schema.transformOrFail(
    Schema.Unknown,
    Schema.Unknown as Schema.Schema<A, A, never>,
    {
      strict: true,
      decode: (input, _, ast) => {
        const result = zodSchema.safeParse(input);
        if (result.success) {
          return ParseResult.succeed(result.data);
        }
        return ParseResult.fail(
          new ParseResult.Type(
            ast,
            input,
            result.error.issues.map((i) => i.message).join("; "),
          ),
        );
      },
      encode: ParseResult.succeed,
    },
  );
}

/**
 * Converts an Effect Schema to a Zod schema.
 * This allows using Effect Schema types in Zod validation pipelines.
 */
export function effectSchemaToZod<A, I = unknown>(
  effectSchema: Schema.Schema<A, I, never>,
): z.ZodType<A> {
  return z.unknown().transform((val, ctx): A => {
    const result = Schema.decodeUnknownEither(effectSchema, { errors: "all" })(
      val,
    );
    if (result._tag === "Right") {
      return result.right;
    }

    for (const issue of ParseResult.ArrayFormatter.formatErrorSync(
      result.left,
    )) {
      ctx.addIssue({
        code: "custom",
        message: issue.message,
        path: [...issue.path],
      });
    }
    return z.NEVER;
  });
}
